import { collabStateManager } from '@databases/collab-state-manager';
import { DatabaseSdk } from '@databases/sdk';
import { container } from '@databases/service-container';
import { tableViewLayoutModule } from '@databases/table-layout/view-layout-module';
import { createTimelineViewLayoutModule } from '@databases/timeline-layout/view-layout-module';
import {
  createViewLayoutRegistry,
  type ViewLayoutRegistry,
} from '@databases/view-layout-modules';
import { createTableV2LayoutModule } from '@databases/view-layout-table-v2';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type Recyclable } from './types.ts';

/**
 * Timeline layout writes are gated on the `databasesRoadmap` experiment. When
 * off (the default), the Timeline module is registered `read-only`: persisted
 * Timeline settings still decode and round-trip, but new writes are blocked
 * (rollback-safe).
 *
 * Exposure is intentionally not fired here (`fireExperimentExposure: false`):
 * this runs at SDK/registry construction, not at the point the Timeline
 * experience diverges.
 */
const isTimelineLayoutEnabled = (): boolean =>
  getExperimentValue('databasesRoadmap', 'isEnabled', {
    fireExperimentExposure: false,
  });

/**
 * Table V2 writes are gated at registry construction. Read-only mode preserves
 * persisted layout data when the experiment is disabled.
 */
const isTableV2LayoutEnabled = (): boolean =>
  getExperimentValue('databasesTableLayoutV2', 'isEnabled', {
    fireExperimentExposure: false,
  });

/**
 * Assembles the view-layout module registry for the current database. This is
 * the registration point for first-party layout modules. Optional-layout write
 * policy, such as Timeline's experiment-controlled rollback mode, is supplied
 * through the module's `getMode`.
 *
 * Typed as the base {@link ViewLayoutRegistry} so the resulting `DatabaseSdk`
 * stays assignable to the container's SDK contract. The registry still drives
 * converters and write policy at runtime regardless of the static type.
 *
 * Exported for testing the experiment-driven Timeline write policy.
 */
export const createDatabaseViewLayoutRegistry = (): ViewLayoutRegistry =>
  createViewLayoutRegistry({
    modules: [
      tableViewLayoutModule,
      createTimelineViewLayoutModule({
        getMode: () => (isTimelineLayoutEnabled() ? 'read-write' : 'read-only'),
      }),
      createTableV2LayoutModule({
        getMode: () => (isTableV2LayoutEnabled() ? 'read-write' : 'read-only'),
      }),
    ],
  });

/**
 * Recyclable that exposes the current database's {@link DatabaseSdk} through
 * the service container. The SDK is built from the active collab manager's
 * YDatabase whenever a manager attaches (or re-attaches) for the content ID,
 * and cleared on dispose so stale instances are never cached across identity
 * changes.
 */
export function initializeCurrentDatabaseSdk(): Recyclable {
  let unsubscribe: (() => void) | undefined;

  return {
    setup(contentId: string) {
      unsubscribe = collabStateManager.onCollabManagerAttached(
        contentId,
        (collabManager) => {
          container.registerSingleton(
            'DatabaseSdk',
            () =>
              new DatabaseSdk(collabManager.database, {
                viewLayoutRegistry: createDatabaseViewLayoutRegistry(),
              }),
          );
        },
      );
    },

    dispose() {
      unsubscribe?.();
      unsubscribe = undefined;
      container.unregister('DatabaseSdk');
    },
  };
}
